<template>
  <div v-loading="dataLoading" class="app-content-wrapper">
    <div class="search-wrapper">
      <search-component
        ref="search"
        class="dispute-search"
        :search-condition="searchCondition"
        :search-buttons="searchButtons"
        :searchButtonMaxNum="3"
        :fuzzySearchPlaceholder="$t('message.alert.rdrId')"
        :fuzzySearchKey="'caseId'"
        :isDisplayReset="true"
        router-name="/risk/rdr/alerts/list"
      >
      </search-component>
    </div>
    <div class="table-wrapper">
      <custom-table
        :table-set="tableCondition"
        :service-function="queryRDRAlertList"
        ref="table"
        :is-first-automatic-visit="false"
        router-name="/risk/rdr/alerts/list"
      >
      </custom-table>
    </div>
  </div>
</template>
<script>
import { mapGetters } from 'vuex';

import { ShowSuccessMessage } from '@/components/modal/tips/message-tips.js';
import CustomTable from '@/components/table/custom-table.vue';
import SearchComponent from '@/components/table/search-component.vue';
import { REQUIRED } from '@/components/validation/tool/validator';
import GlobalData from '@/utils/global/index';
import {
  getEnantiomericValue, initSelectedData,
} from '@/utils/tools/constant';
import { queryRDRAlertList, downloadRDRAlertList } from '@/views/risk/api/rdr';

export default {
  components: {
    SearchComponent,
    CustomTable,
  },
  computed: {
    ...mapGetters([
      'app',
    ]),
  },
  data() {
    return {
      dataLoading: false,
      checkedAll: false,
      searchCondition: [
        {
          key: 'caseTypes',
          name: 'message.alert.alertType',
          type: 'select',
          defValue: [''],
          multiple: true,
          isInternationalization: true,
          options: [],
          changeValue: (value) => {
            let list = [];
            if ((value[value.length - 1] === '')) {
              list = [''];
            } else {
              list = value.filter(item => item !== '');
            }
            this.$refs.search.setInputValue('caseTypes', list);
          },
        },
        {
          key: 'resellerSubMerchantId',
          name: 'message.alert.resellerSubMerchantId',
          type: 'input',
          defValue: '',
          placeholder: 'message.alert.resellerSubMerchantIdInputTips',
        },
        {
          key: 'dba',
          name: 'message.alert.DBA',
          type: 'input',
          defValue: '',
          placeholder: 'message.alert.DBAInputTips',
        },
        {
          key: 'bin',
          name: 'message.alert.BIN',
          type: 'input',
          defValue: '',
          placeholder: 'message.alert.BINInputTips',
        },
        {
          key: 'caid',
          name: 'message.alert.CAID',
          type: 'input',
          defValue: '',
          placeholder: 'message.alert.CAIDInputTips',
        },
        {
          key: 'merchantOrderId',
          name: 'message.alert.merchantOrderId',
          type: 'input',
          defValue: '',
          placeholder: 'message.alert.merchantOrderId',
        },
        {
          key: 'arn',
          name: 'message.alert.arn',
          type: 'input',
          defValue: '',
          placeholder: 'message.alert.arn',
        },
        {
          key: 'statusList',
          name: 'message.alert.outcomeStatus',
          type: 'select',
          defValue: [],
          multiple: true,
          isInternationalization: true,
          options: [],
        },
        {
          key: ['caseReceivedDateStart', 'caseReceivedDateEnd'],
          name: 'message.alert.caseReceivedDate',
          defValue: ['', ''],
          type: 'date_time',
        },
        {
          key: ['txnTimeStart', 'txnTimeEnd'],
          name: 'message.alert.authTime',
          defValue: ['', ''],
          type: 'date_time',
        },
      ],
      searchButtons: [
        {
          type: 'download',
          permissionCode: 'risk:rdr:alerts:list:download',
          func: async (param) => {
            if (!this.downloading) {
              this.downloading = true;
              try {
                const res = await downloadRDRAlertList(param);
                if (res) {
                  window.location.href = res;
                  ShowSuccessMessage(this.$t('message.downloadSuccessfulTip'));
                }
              } catch (err) {
                //
              }
              this.downloading = false;
            }
          },
        },
      ],
      tableCondition: [
        {
          titleBar: {
            type: 'text',
            title: 'message.alert.caseReceivedDate',
          },
          key: 'caseReceivedDate',
          type: 'text',
        },
        {
          titleBar: {
            type: 'text',
            title: 'message.alert.rdrId',
          },
          key: 'caseId',
          type: 'text',
        },
        {
          titleBar: {
            type: 'text',
            title: 'message.alert.alertType',
          },
          key: 'caseType',
          type: 'text',
          textFunc: (data) => getEnantiomericValue(this.alertTypeList, data.caseType),
        },
        {
          titleBar: {
            type: 'text',
            title: 'message.alert.authTime',
          },
          key: 'txnTime',
          type: 'text',
        },
        {
          titleBar: {
            type: 'text',
            title: 'message.alert.amount',
          },
          key: 'txnAmount',
          type: 'text',
        },
        {
          titleBar: {
            type: 'text',
            title: 'message.alert.currency',
          },
          key: 'txnCurrency',
          type: 'text',
        },
        {
          titleBar: {
            type: 'text',
            title: 'message.alert.resellerSubMerchantId',
          },
          key: 'resellerSubMerchantId',
          type: 'text',
        },
        {
          titleBar: {
            type: 'text',
            title: 'message.alert.BIN',
          },
          key: 'bin',
          type: 'text',
        },
        {
          titleBar: {
            type: 'text',
            title: 'message.alert.CAID',
          },
          key: 'caid',
          type: 'text',
        },
        {
          titleBar: {
            type: 'text',
            title: 'message.alert.DBA',
          },
          key: 'dba',
          type: 'text',
        },
        {
          titleBar: {
            type: 'text',
            title: 'message.alert.merchantOrderId',
          },
          key: 'merchantOrderId',
          type: 'text',
        },
        {
          titleBar: {
            type: 'text',
            title: 'message.alert.arn',
          },
          key: 'arn',
          type: 'text',
        },
        {
          titleBar: {
            type: 'text',
            title: 'message.alert.outcomeStatus',
          },
          key: 'status',
          type: 'text',
          textFunc: (data) => getEnantiomericValue(this.outcomeStatusList, data.status),
        },
        {
          titleBar: {
            type: 'text',
            title: 'message.alert.pricingTier',
          },
          key: 'pricingTier',
          type: 'text',
        },
        {
          titleBar: {
            type: 'text',
            title: 'message.alert.updateDate',
          },
          key: 'updateTime',
          type: 'text',
        },
        {
          key: 'buttons',
          titleBar: {
            title: 'message.tableCommon.operation',
            type: 'operations',
          },
          type: 'operations',
          subTableDisplays: [
            {
              type: 'text_button',
              textFunc: () => 'message.tableCommon.details',
              permissionCode: 'risk:rdr:alerts:list:see',
              func: (data) => {
                this.$router.push({
                  path: '/risk/rdr/alerts/view',
                  query: {
                    id: data.id,
                  },
                });
              },
            },
          ],
        },
      ],
      outcomeStatusList: [],
      alertTypeList: [],
    };
  },
  mounted() {
    this.initMethod();
  },
  methods: {
    queryRDRAlertList,
    async initMethod() {
      this.dataLoading = true;
      try {
        const dict = await GlobalData.getDictList(['RDR_ACW_STATUS', 'RDR_ACW_CASE_TYPE']);
        this.outcomeStatusList = initSelectedData(dict.RDR_ACW_STATUS, 'description', 'value', false);
        this.alertTypeList = initSelectedData(dict.RDR_ACW_CASE_TYPE);
        this.$refs.search.setSearchSelectOptions('statusList', this.outcomeStatusList);
        this.$refs.search.setSearchSelectOptions('caseTypes', this.alertTypeList);
        if (this.$refs.table) {
          this.$refs.table.firstVisitHttp();
        }
      } catch (err) {
        //
      }
      this.dataLoading = false;
    },
  },
};
</script>
<style lang="scss">
</style>
